import { normalizeFitRef, type PublicConfigSummary } from "@cfg/api-contract";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useApp } from "../app-context.tsx";
import { DefaultCatalogCard, DefaultEmptyState } from "./defaults.tsx";

/** Published configurations, filterable by fit ref (e.g. a LEGO set number) and design. */
export function CommunityPage() {
  const { api, brand, copy, registry } = useApp();
  const Card = brand.slots?.CatalogCard ?? DefaultCatalogCard;
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const [search, setSearch] = useSearchParams();
  const fit = search.get("fit") ?? "";
  const design = search.get("design") ?? "";
  const [text, setText] = useState(fit);

  const catalog = useQuery({ queryKey: ["catalog", brand.config.id], queryFn: () => registry.catalog() });
  const fits = useQuery({ queryKey: ["fits", brand.config.id], queryFn: () => api!.listFits(), enabled: !!api });
  const feed = useInfiniteQuery({
    queryKey: ["community", brand.config.id, fit, design],
    queryFn: ({ pageParam }) => api!.listPublic({ fit: fit || undefined, design: design || undefined, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor,
    enabled: !!api,
  });

  if (!api) return <Empty title={copy("community.title")} body="This deployment has no API configured." />;

  const designs = new Map((catalog.data ?? []).map((d) => [d.id, d]));
  const items: PublicConfigSummary[] = feed.data?.pages.flatMap((p) => p.items) ?? [];
  const setFilter = (next: { fit?: string; design?: string }) => {
    const p = new URLSearchParams();
    const f = next.fit ?? fit;
    const d = next.design ?? design;
    if (f) p.set("fit", normalizeFitRef(f));
    if (d) p.set("design", d);
    setSearch(p);
  };
  const describe = (it: PublicConfigSummary) =>
    [
      designs.get(it.designId)?.title ?? it.designId,
      copy("community.by", { name: it.authorName }),
      it.printed ? `✓ ${copy("community.printed")}` : null,
      it.confirmCount > 0 ? copy("community.confirms", { count: it.confirmCount }) : null,
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <section className="page page-community">
      <header className="page-head">
        <h1>{copy("community.title")}</h1>
        <p className="page-sub">{copy("community.sub")}</p>
        <form
          className="community-filters"
          onSubmit={(e) => {
            e.preventDefault();
            setFilter({ fit: text });
          }}
        >
          <input className="cfg-input" aria-label={copy("community.search")} placeholder={brand.config.fitTags.refPlaceholder || copy("community.search")} value={text} onChange={(e) => setText(e.target.value)} />
          <select className="cfg-input" aria-label={copy("community.allDesigns")} value={design} onChange={(e) => setFilter({ design: e.target.value })}>
            <option value="">{copy("community.allDesigns")}</option>
            {(catalog.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.title}
              </option>
            ))}
          </select>
          <button type="submit" className="cfg-btn">
            {copy("community.search")}
          </button>
          {(fit || design) && (
            <button
              type="button"
              className="cfg-chip"
              onClick={() => {
                setText("");
                setSearch(new URLSearchParams());
              }}
            >
              ×
            </button>
          )}
        </form>
        {fits.data && fits.data.fits.length > 0 && (
          <div className="community-popular">
            <span className="cfg-presets-label">{copy("community.popular")}</span>
            {fits.data.fits.slice(0, 20).map((f) => (
              <button
                key={f.ref}
                type="button"
                className={`cfg-chip${f.ref === fit ? " is-active" : ""}`}
                title={f.name}
                onClick={() => {
                  setText(f.ref);
                  setFilter({ fit: f.ref });
                }}
              >
                {f.ref} ({f.count})
              </button>
            ))}
          </div>
        )}
      </header>

      {feed.isLoading && <p className="muted">Loading…</p>}
      {feed.isError && <Empty title="Could not load community configurations" body={String(feed.error)} />}
      {feed.isSuccess && items.length === 0 && <Empty title={copy("community.empty")} />}
      {items.length > 0 && (
        <div className="catalog-grid">
          {items.map((it) => (
            <Card
              key={it.id}
              entry={{
                id: it.id,
                version: it.version,
                title: it.name,
                description: describe(it),
                tags: it.fits.map((f) => f.ref),
                thumbnailUrl: designs.get(it.designId)?.thumbnailUrl,
                href: `/community/${encodeURIComponent(it.id)}`,
              }}
            />
          ))}
        </div>
      )}
      {feed.hasNextPage && (
        <p>
          <button type="button" className="cfg-btn" disabled={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()}>
            {copy("community.more")}
          </button>
        </p>
      )}
      {brand.config.fitTags.disclaimer && <p className="muted small community-legal">{brand.config.fitTags.disclaimer}</p>}
    </section>
  );
}
